<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Icon from '@/Components/Utils/Icon.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faExternalLink } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
import { useFormatTime } from '@/Composables/useFormatTime'

library.add(faExternalLink)

defineProps<{
    title?: string
    pageHead: PageHeadingTS
    data: {
        id: number
        name: string
        brand_name?: string
        brand_slug?: string
        product_name?: string
        link: string
        note?: string
        creator_name?: string
        link_updated_at?: string
        created_at?: string
    }
    orders: {
        id: number
        slug: string
        reference: string
        state_label?: string
        state_icon?: any
        invoice_date?: string
        attached_at?: string
    }[]
}>()
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead" />

    <div class="grid gap-4 px-4 py-4 md:grid-cols-2">
        <div class="h-fit overflow-hidden rounded-xl border border-sky-400">
            <div class="flex items-start justify-between gap-x-4 border-b border-gray-900/5 bg-gray-50 p-6">
                <div class="min-w-0">
                    <div class="font-bold">{{ data.name }}</div>
                    <Link v-if="data.brand_slug" :href="route('kasir.brands.show', data.brand_slug)" class="text-sm secondaryLink">
                        {{ data.brand_name }}
                    </Link>
                </div>
                <a :href="data.link" target="_blank" rel="noopener" class="shrink-0 text-sm secondaryLink">
                    <FontAwesomeIcon icon="fal fa-external-link" fixed-width aria-hidden="true" />
                    Buka
                </a>
            </div>

            <dl class="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm/6">
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Produk</dt>
                    <dd v-if="data.product_name" class="text-right font-medium">{{ data.product_name }}</dd>
                    <dd v-else class="text-right italic text-gray-400">Semua produk</dd>
                </div>
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Link diupdate</dt>
                    <dd class="text-right font-medium">{{ useFormatTime(data.link_updated_at, { formatTime: 'hm' }) }}</dd>
                </div>
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Dibuat</dt>
                    <dd class="text-right font-medium">{{ useFormatTime(data.created_at, { formatTime: 'hm' }) }}</dd>
                </div>
                <div v-if="data.creator_name" class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Dibuat oleh</dt>
                    <dd class="text-right font-medium">{{ data.creator_name }}</dd>
                </div>
                <div v-if="data.note" class="py-3">
                    <dt class="mb-1 text-gray-500">Catatan</dt>
                    <dd class="text-gray-700">{{ data.note }}</dd>
                </div>
            </dl>
        </div>

        <div class="h-fit overflow-hidden rounded-xl border border-gray-200">
            <div class="border-b border-gray-900/5 bg-gray-50 px-6 py-3">
                <div class="font-semibold text-gray-700">Dipakai di pesanan</div>
                <div class="text-xs text-gray-400">{{ orders.length }} pesanan</div>
            </div>
            <div v-if="!orders.length" class="px-6 py-6 text-center text-sm text-gray-400">
                Desain ini belum dipakai di pesanan manapun
            </div>
            <ul v-else class="divide-y divide-gray-100 text-sm">
                <li v-for="order in orders" :key="order.id" class="flex items-start justify-between gap-x-4 px-6 py-3">
                    <div class="min-w-0">
                        <Link :href="route('kasir.orders.show', order.slug)" class="font-mono font-medium primaryLink">
                            {{ order.reference }}
                        </Link>
                        <div class="text-xs text-gray-400">Invoice {{ useFormatTime(order.invoice_date) }}</div>
                    </div>
                    <div class="shrink-0 text-right">
                        <div class="flex items-center justify-end gap-x-1.5">
                            <Icon v-if="order.state_icon" :data="order.state_icon" />
                            <span class="font-medium">{{ order.state_label }}</span>
                        </div>
                        <div class="text-xs text-gray-400">
                            Ditambahkan {{ useFormatTime(order.attached_at, { formatTime: 'hm' }) }}
                        </div>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>
